// QueryView：统一处理 useQuery 的 loading / error / empty / data 四态。
// 用法：<QueryView query={q}>{(data) => <Table rows={data.items} />}</QueryView>

import type { ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { ErrorPanel } from "./ErrorPanel";
import { Empty } from "./Empty";

interface QueryViewProps<T> {
  query: UseQueryResult<T>;
  loading?: ReactNode;
  empty?: ReactNode;
  error?: (err: unknown, retry: () => void) => ReactNode;
  isEmpty?: (data: T) => boolean;
  children: (data: T) => ReactNode;
}

function DefaultSkeleton() {
  return (
    <div
      role="status"
      aria-label="加载中"
      className="space-y-2 animate-pulse"
    >
      <div className="h-6 w-1/3 bg-[hsl(var(--muted))] rounded" />
      <div className="h-4 w-full bg-[hsl(var(--muted))] rounded" />
      <div className="h-4 w-5/6 bg-[hsl(var(--muted))] rounded" />
      <div className="h-4 w-4/6 bg-[hsl(var(--muted))] rounded" />
    </div>
  );
}

export function QueryView<T>({
  query,
  loading,
  empty,
  error,
  isEmpty,
  children,
}: QueryViewProps<T>) {
  if (query.isLoading || (query.isFetching && !query.data)) {
    return <>{loading ?? <DefaultSkeleton />}</>;
  }
  if (query.isError) {
    return (
      <>
        {error
          ? error(query.error, () => query.refetch())
          : <ErrorPanel error={query.error} retry={() => query.refetch()} />}
      </>
    );
  }
  const data = query.data as T | undefined;
  if (data === undefined || data === null) {
    return <>{empty ?? <Empty title="暂无数据" />}</>;
  }
  if (isEmpty?.(data)) {
    return <>{empty ?? <Empty title="暂无数据" />}</>;
  }
  return <>{children(data)}</>;
}
